/** @jsx h */
import { component, h } from 'literaljs';
import { playPop } from '../../utils/sounds';

const changelogData = [
	{
		version: 'v8.1.11',
		date: '2026-07-21',
		type: 'patch',
		gitTag: 'v8.1.11',
		changes: [
			{ type: 'fix', text: 'patch() no longer clears innerHTML when both old and new children arrays are empty — externally managed DOM containers (demo instances) survive re-renders' }
		]
	},
	{
		version: 'v8.1.10',
		date: '2026-07-21',
		type: 'patch',
		gitTag: 'v8.1.10',
		changes: [
			{ type: 'fix', text: 'Reverted cache-hit clone elimination that broke nested component state updates — deep-clone restored on cache hits while retaining all other 8.1.9 improvements' }
		]
	},
	{
		version: 'v8.1.9',
		date: '2026-07-21',
		type: 'patch',
		gitTag: 'v8.1.9',
		changes: [
			{ type: 'fix', text: 'Keyed reconciliation no longer reverses consecutive moved nodes — sortable lists, prepends, and drag-and-drop now preserve correct order' },
			{ type: 'fix', text: 'Stale attributes (including dropped style and events) are now properly removed from the DOM on update' },
			{ type: 'fix', text: 'Initial null/false root render now mounts content correctly on subsequent renders' },
			{ type: 'fix', text: 'Throwing lifecycle hooks no longer brick the app — flush is now isolated with per-callback error handling' },
			{ type: 'fix', text: 'Error boundary no longer caches failed renders — components recover when the error condition clears' },
			{ type: 'fix', text: 'updated() lifecycle hook now fires only after actual DOM changes, not on no-op patches' },
			{ type: 'fix', text: 'style attribute now accepts raw strings in addition to objects' },
			{ type: 'perf', text: 'Component instance persistence — instances created once per key and reused, enabling stable method references and row-level memoization' },
			{ type: 'perf', text: 'Eliminated per-render cloning — h() no longer clones attrs (props are now immutable); memoized subtrees returned by reference. GC was 25.7% of select-row profile' },
			{ type: 'perf', text: 'Event rebind guard — listeners no longer unbound/rebound when events object is reference-identical (~3,000 fewer rebinds per 1,000-row update)' },
			{ type: 'perf', text: 'Instance records disposed on unmount — fixes slow memory leak of cached render trees for removed components' },
			{ type: 'perf', text: 'Measured (Chrome, 4× throttle): select row 6.2× faster, update-every-10th 3.2×, swap rows 3.0×, remove row 2.2×' },
			{ type: 'docs', text: 'Props are now immutable by contract (h() no longer clones). Documented immutability, conditional-sibling key warning, event delegation semantics, and stable-handler perf tip' }
		]
	},
	{
		version: 'v8.1.8',
		date: '2026-06-09',
		type: 'patch',
		gitTag: 'v8.1.8',
		changes: [
			{ type: 'fix', text: 'State updates now correctly trigger DOM updates when component instances are returned directly from render()' },
			{ type: 'fix', text: 'Fixed memoization to preserve component instances instead of flattening them into VNodes' }
		]
	},
	{
		version: 'v8.0.5',
		date: '2026-05-12',
		type: 'patch',
		gitTag: 'v8.0.5',
		changes: [
			{ type: 'fix', text: 'Fixed falsy children reconciliation bug — conditional rendering with && now works correctly' },
			{ type: 'fix', text: 'flatten() now skips null, undefined, false, and true values' },
			{ type: 'fix', text: 'areNodesEqual() is now null-safe' }
		]
	},
	{
		version: 'v8.0.4',
		date: '2026-05-08',
		type: 'patch',
		gitTag: 'v8.0.4',
		changes: [
			{ type: 'fix', text: 'Improved keyed reconciliation performance' },
			{ type: 'fix', text: 'Better handling of sibling component updates' }
		]
	},
	{
		version: 'v8.0.0',
		date: '2026-04-15',
		type: 'major',
		gitTag: 'v8.0.0',
		changes: [
			{ type: 'feature', text: 'Auto-keyed sibling components — no manual key props needed for most cases' },
			{ type: 'feature', text: 'Render memoization — shallow props comparison skips re-execution when props unchanged' },
			{ type: 'feature', text: 'New App container with isolated state, events, and lifecycle per instance' },
			{ type: 'feature', text: 'CustomEvent-based re-render system' },
			{ type: 'feature', text: 'Improved VNode structure with better type safety' },
			{ type: 'perf', text: '91% faster replace 1,000 rows (65ms → 6ms)' },
			{ type: 'perf', text: '99.8% faster select row (22ms → 0.04ms)' },
			{ type: 'perf', text: '93% faster remove row (74ms → 5ms)' },
			{ type: 'perf', text: '99.7% faster clear rows (3ms → 0.01ms)' },
			{ type: 'breaking', text: 'API changed from render(App, "root", store) to new App(App, store).mount("root")' }
		]
	},
	{
		version: 'v7.0.0',
		date: '2023-06-01',
		type: 'major',
		gitTag: 'v7.0.0',
		changes: [
			{ type: 'feature', text: 'Initial release of LiteralJS framework' },
			{ type: 'feature', text: 'Component-based architecture with JSX support' },
			{ type: 'feature', text: 'State management with setState/getState' },
			{ type: 'feature', text: 'Event handling with events prop' },
			{ type: 'feature', text: 'Lifecycle methods: mounted, updated, unmounted' }
		]
	}
];

const typeLabels = {
	feature: 'Feature',
	fix: 'Fix',
	perf: 'Performance',
	breaking: 'Breaking',
	docs: 'Docs'
};

const typeColors = {
	feature: 'var(--ctp-green)',
	fix: 'var(--ctp-red)',
	perf: 'var(--ctp-yellow)',
	breaking: 'var(--ctp-peach)',
	docs: 'var(--ctp-blue)'
};

const groupOrder = ['breaking', 'feature', 'perf', 'fix', 'docs'];
const groupLabels = {
	breaking: 'Breaking Changes',
	feature: 'Added',
	perf: 'Performance',
	fix: 'Fixed',
	docs: 'Documentation'
};

function formatDate(iso) {
	const d = new Date(iso);
	return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
}

function groupChanges(changes) {
	const groups = {};
	changes.forEach(c => {
		if (!groups[c.type]) groups[c.type] = [];
		groups[c.type].push(c);
	});
	return groups;
}

export default component({
	name: 'ChangelogPage',
	state: {
		filter: 'all',
		majorOnly: false
	},
	methods() {
		return {
			setFilter(filter) {
				playPop();
				this.setState({ filter });
			},
			toggleMajorOnly() {
				playPop();
				this.setState({ majorOnly: !this.getState().majorOnly });
			}
		};
	},
	render() {
		const { filter, majorOnly } = this.getState();

		let filteredData = changelogData;
		if (majorOnly) {
			filteredData = filteredData.filter(r => r.type === 'major');
		}
		if (filter !== 'all') {
			filteredData = filteredData.map(release => ({
				...release,
				changes: release.changes.filter(c => c.type === filter)
			})).filter(release => release.changes.length > 0);
		}

		const hasBreaking = (changes) => changes.some(c => c.type === 'breaking');

		return (
			<div class="page-changelog">
				<section class="changelog-hero">
					<div class="changelog-hero-inner">
						<div class="changelog-hero-text">
							<h1>Changelog</h1>
							<p class="section-subtitle">What's new in LiteralJS</p>
						</div>
						<a
							class="changelog-rss"
							href="https://gitlab.com/literaljs/literaljs/-/releases.atom"
							target="_blank"
							rel="noopener noreferrer"
						>
							Subscribe to RSS
						</a>
					</div>

					<div class="changelog-toolbar">
						<div class="changelog-filters">
							{['all', 'feature', 'fix', 'perf', 'breaking'].map(type => (
								<button
									key={type}
									class={"filter-btn" + (filter === type ? ' -active' : '')}
									style={{
										borderColor: filter === type ? typeColors[type] || 'var(--accent)' : 'transparent'
									}}
									events={{ click: () => this.setFilter(type) }}
								>
									{typeLabels[type] || 'All'}
								</button>
							))}
						</div>
						<label class="version-filter">
							<input
								type="checkbox"
								checked={majorOnly}
								events={{ change: () => this.toggleMajorOnly() }}
							/>
							<span>Major only</span>
						</label>
					</div>
				</section>

				<section class="changelog-list">
					{filteredData.length === 0 ? (
						<div class="changelog-empty">
							<div class="changelog-empty-icon">
								<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
									<path d="M9 14l2 2 4-4"/>
									<circle cx="12" cy="12" r="10"/>
								</svg>
							</div>
							<h3>No changes match this filter</h3>
							<p>Try selecting a different change type or unchecking "Major only".</p>
						</div>
					) : (
						filteredData.map((release, index) => {
							const groups = groupChanges(release.changes);
							const isLatest = index === 0;
							return (
								<div key={release.version} class={"changelog-release" + (isLatest ? ' release-latest' : '')}>
									<div class="release-header">
										<div class="release-header-left">
											<h2 class="release-version">{release.version}</h2>
											{isLatest && <span class="release-latest-badge">Latest</span>}
										</div>
										<div class="release-header-right">
											<span class="release-date">{formatDate(release.date)}</span>
											<span class={"release-badge -" + release.type}>{release.type}</span>
											<a
												class="release-gitlab"
												href={`https://gitlab.com/literaljs/literaljs/-/tags/${release.gitTag}`}
												target="_blank"
												rel="noopener noreferrer"
											>
												View on GitLab
											</a>
										</div>
									</div>

									{hasBreaking(release.changes) && (
										<div class="breaking-callout">
											<div class="breaking-callout-icon">
												<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
													<path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
													<line x1="12" y1="9" x2="12" y2="13"/>
													<line x1="12" y1="17" x2="12.01" y2="17"/>
												</svg>
											</div>
											<div class="breaking-callout-content">
												<strong>Breaking Changes</strong>
												<p>This release contains breaking changes. Review before upgrading.</p>
											</div>
										</div>
									)}

									<div class="release-groups">
										{groupOrder.map(groupType => {
											const items = groups[groupType];
											if (!items || items.length === 0) return null;
											return (
												<div key={groupType} class="change-group" style={{ borderLeftColor: typeColors[groupType] || 'var(--text-muted)' }}>
													<h4 class="change-group-title" style={{ color: typeColors[groupType] || 'var(--text-muted)' }}>
														{groupLabels[groupType] || groupType}
													</h4>
													<ul class="change-group-list">
														{items.map((change, i) => (
															<li key={i} class="change-item">
																<span class="change-text">{change.text}</span>
															</li>
														))}
													</ul>
												</div>
											);
										})}
									</div>
								</div>
							);
						})
					)}
				</section>
			</div>
		);
	}
});
